export type KioskSessionSummary = {
  id: string;
  driverName: string;
  trackName: string;
  recordedAtMs: number | null;
  lapCount: number;
  bestLapMs: number | null;
};

export type KioskSessionPage = {
  data: KioskSessionSummary[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  snapshot: string;
};

export type KioskSessionDirectory = Omit<KioskSessionPage, 'data'> & {
  sessions: KioskSessionSummary[];
};

const INCONSISTENT_DIRECTORY_MESSAGE =
  'The session list changed while loading. Reload it to continue.';

function assertValidPage(page: KioskSessionPage, expectedPage: number) {
  const expectedTotalPages = Math.max(Math.ceil(page.total / page.perPage), 1);
  const expectedLoadedCount = Math.min(page.page * page.perPage, page.total);

  if (
    page.page !== expectedPage ||
    page.page < 1 ||
    page.perPage < 1 ||
    page.perPage > 100 ||
    page.total < 0 ||
    page.totalPages !== expectedTotalPages ||
    page.page > page.totalPages ||
    page.data.length > page.perPage ||
    page.data.length !== expectedLoadedCount - (page.page - 1) * page.perPage ||
    !/^[A-Za-z0-9_-]{43}$/.test(page.snapshot)
  ) {
    throw new Error(INCONSISTENT_DIRECTORY_MESSAGE);
  }
}

export function mergeKioskSessionPage(
  current: KioskSessionDirectory | null,
  page: KioskSessionPage,
): KioskSessionDirectory {
  const expectedPage = current ? current.page + 1 : 1;
  assertValidPage(page, expectedPage);

  if (
    current &&
    (current.snapshot !== page.snapshot ||
      current.perPage !== page.perPage ||
      current.total !== page.total ||
      current.totalPages !== page.totalPages)
  ) {
    throw new Error(INCONSISTENT_DIRECTORY_MESSAGE);
  }

  const sessions = [...(current?.sessions ?? []), ...page.data];
  if (new Set(sessions.map((session) => session.id)).size !== sessions.length) {
    throw new Error(INCONSISTENT_DIRECTORY_MESSAGE);
  }

  return {
    sessions,
    page: page.page,
    perPage: page.perPage,
    total: page.total,
    totalPages: page.totalPages,
    snapshot: page.snapshot,
  };
}

export function kioskSessionDirectoryHasMore(directory: KioskSessionDirectory | null) {
  return Boolean(directory && directory.page < directory.totalPages);
}

export function kioskSessionDirectorySessions(
  directory: KioskSessionDirectory | null,
  activeSession: KioskSessionSummary | null,
) {
  const sessions = directory?.sessions ?? [];
  if (!activeSession || sessions.some((session) => session.id === activeSession.id)) {
    return sessions;
  }
  return [...sessions, activeSession];
}
